import React, { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  FormControl,
  Box,
  FormControlLabel,
  Checkbox,
  Grid
} from '@mui/material';
import { Typography } from '@mui/material';
import { useFormik } from 'formik';
import { addDays, format } from 'date-fns';

import { DialogTitle, DialogContent, DialogActions } from '@/components/Dialog';
import { BulletList, BulletListItem } from '@/components/BulletList';
import { callRest } from '@/common/utils/utils';
import ConvertMembershipModal from '../../../../../Customer/CustomerProfile/CustomerTabs/Membership/Modal/ConvertMembershipModal';
import { useMembershipEvents } from './useMembershipEvents';
import { useLatestMembershipEvent } from './useLatestMembershipEvent';
import {
  FreezeMembershipConfirmation,
  MembershipCustomer
} from './FreezeMembershipConfirmation';
import { DateInput } from '@/components/DateInput';
import { ListSubheader } from '@mui/material';

export type Membership = {
  id: number;
  customerName: string;
  description: string;
  isLive: boolean;
  isFrozen: number;
  nextBillDateReadable: string;
  nextBillDate: string;
  cancelDate?: string;
  cancelDateReadable: any;
  freezeStartDate?: string;
  freezeEndDate?: string;
  freezeStartDateReadable: any;
  status: string;
  maxDuration: number;
  membershipId: number;
  lastBilledDate: string;
  convertedTo: number;
  billingAmount: number;
  cancellationReason?: number;
};

type Props = {
  customerMembership: Membership;
  onClose: () => void;
  onMembershipChange: () => Promise<void>;
  setRefreshList: any;
  isPaymentMethodOnFile: boolean;
  customerData: MembershipCustomer;
  eventReasons: any;
};

const Endpoint = {
  Freeze: '/pos_customer/freeze_membership/',
  Unfreeze: '/pos_customer/unfreeze_membership/',
  Cancel: '/pos_customer/cancel_membership/',
  Uncancel: '/pos_customer/uncancel_membership/',
  SetNextBillDate: '/pos_customer/set_next_bill_date_membership/',
  MembershipUpdate:
    '/pos_customer/update_next_bill_date_and_price_for_membership/'
} as const;

const Status = {
  Active: 'Active',
  Frozen: 'Frozen',
  Canceled: 'Canceled',
  Unknown: 'Unknown',
  ActivatesInFuture: 'ActiveInFuture'
};

const Action = {
  Freeze: 'Freeze',
  Unfreeze: 'Unfreeze',
  Cancel: 'Cancel',
  Uncancel: 'Uncancel',
  Convert: 'Convert',
  Update: 'Update'
};

export const reasonTypes = {
  financial: 'Financial Reasons',
  dissatisfaction: 'Service or Product Dissatisfaction',
  satisfaction: 'Service or Product Satisfaction',
  personal: 'Personal or Lifestyle Changes',
  competitor: 'Competitor-Driven Reasons',
  suspension: 'Temporary Suspension',
  adjustments: 'Temporary Adjustments',
  other: 'Other Reasons',
  reasons: 'Reasons'
};

const monthsDelta = (start, end) => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  return (
    (endDate.getFullYear() - startDate.getFullYear()) * 12 +
    (endDate.getMonth() - startDate.getMonth())
  );
};

function EditMembershipModal({
  customerMembership,
  onClose,
  onMembershipChange,
  setRefreshList,
  isPaymentMethodOnFile,
  customerData,
  eventReasons
}: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const [isFreezeConfirmModalOpen, setIsFreezeConfirmModalOpen] =
    useState(false);

  const status = useMemo(() => {
    if (customerMembership.status.includes(Status.Frozen)) {
      return Status.Frozen;
    }

    if (customerMembership.status === Status.Active) {
      return Status.Active;
    }

    if (!customerMembership.isLive) {
      // A membership scheduled to cancel before it ever activates has no
      // "Activates on" text (case 1) and is treated as already Canceled;
      // one that will still activate first (case 2/3) keeps ActivatesInFuture.
      if (customerMembership.status.includes('Activates on')) {
        return Status.ActivatesInFuture;
      }
      return Status.Canceled;
    }

    return customerMembership.status || Status.Unknown;
  }, [customerMembership]);

  const action = useMemo(() => {
    switch (status) {
      case Status.Frozen:
        return Action.Freeze;
      case Status.Canceled:
        return Action.Cancel;
      case Status.ActivatesInFuture:
        return Action.Update;
      case Status.Active:
        return Action.Update;
      default:
        return Action.Update;
    }
  }, [status]);

  const currentAction = useMemo(() => {
    if (status === Status.Frozen) {
      return {
        value: Action.Freeze,
        label: customerMembership.status
      };
    } else if (status === Status.Canceled) {
      return {
        value: Action.Cancel,
        label: Status.Canceled
      };
    } else if (status === Status.ActivatesInFuture || status == Status.Active) {
      return {
        value: Action.Update,
        label: Action.Update
      };
    }
    return {
      value: Action.Update,
      label: customerMembership.status
    };
  }, [customerMembership, status]);

  const freezeAction = useMemo(() => {
    if (status === Status.Canceled || status == Status.ActivatesInFuture) {
      return null;
    }
    if (status === Status.Frozen) {
      return {
        value: Action.Unfreeze,
        label: Action.Unfreeze
      };
    }

    if (
      customerMembership.freezeStartDate &&
      new Date() < new Date(customerMembership.freezeStartDate)
    ) {
      return {
        value: Action.Unfreeze,
        label: 'Remove Scheduled Freeze'
      };
    }

    if (!customerMembership.cancelDate) {
      return {
        value: Action.Freeze,
        label: 'Freeze'
      };
    }
  }, [customerMembership, status]);

  const cancelAction = useMemo(() => {
    if (customerMembership.cancelDate) {
      return {
        value: Action.Uncancel,
        label: 'Remove Cancelation'
      };
    } else {
      return {
        value: Action.Cancel,
        label: Action.Cancel
      };
    }
  }, [customerMembership]);

  const convertAction = {
    value: Action.Convert,
    label: 'Convert'
  };

  const updateAction = {
    value: Action.Update,
    label: 'Update'
  };

  const membershipEvents = useMembershipEvents(customerMembership.id);
  const latestMembershipEvent = useLatestMembershipEvent(membershipEvents);

  useEffect(() => {
    // formik.dirty is intentionally read here rather than added to the deps
    // array below - it guards against overwriting a user's in-progress edit,
    // not something this effect should re-run in response to.
    if (latestMembershipEvent?.membership_event_reason && !formik.dirty) {
      const initialCancelDate = formik.initialValues.cancelDate;
      formik.resetForm({
        values: {
          ...formik.values,
          cancelationReason: latestMembershipEvent?.membership_event_reason,
          cancelDate: initialCancelDate
        }
      });
      // if the cancel date has changed at this point
      // set the value back to what it was
      if (initialCancelDate !== formik.values.cancelDate) {
        formik.setFieldValue('cancelDate', formik.values.cancelDate);
      }
    }
  }, [latestMembershipEvent]);

  async function cancelMembership() {
    await updateMembership({
      endpoint: 'Cancel',
      data: {
        date: formik.values.cancelDate,
        send_email: formik.values.sendCancelationEmail,
        membership_event_reason: formik.values.cancelationReason
      } as any,
      successMessage: 'Cancellation Submitted'
    });
  }

  function onError(e: unknown) {
    enqueueSnackbar(e.toString(), {
      variant: 'error'
    });
  }

  async function updateMembership({
    endpoint,
    data,
    successMessage
  }: {
    endpoint: keyof typeof Endpoint;
    data: {
      date?: string;
      duration?: number;
      price?: number;
    };
    successMessage: string;
  }) {
    formik.setSubmitting(true);
    await callRest(
      Endpoint[endpoint],
      {
        customer_membership: customerMembership.membershipId,
        ...data
      },
      () =>
        enqueueSnackbar(successMessage, {
          variant: 'success'
        }),
      onError
    );
    formik.setSubmitting(false);

    onMembershipChange();
    onClose();
  }

  const formik = useFormik({
    initialValues: {
      action,
      freezeDate: format(Date.now(), 'yyyy-MM-dd'),
      duration:
        status === Status.Frozen
          ? monthsDelta(
              customerMembership.freezeStartDate,
              customerMembership.freezeEndDate
            )
          : 3,
      nextBillDate:
        customerMembership.nextBillDate || format(Date.now(), 'yyyy-MM-dd'),
      price: Number(customerMembership.billingAmount).toFixed(2),
      cancelDate:
        customerMembership.cancelDate ||
        format(addDays(Date.now(), 30), 'yyyy-MM-dd'),
      sendCancelationEmail: true,
      cancelationReason: ''
    },
    onSubmit: async values => {
      switch (values.action) {
        case Action.Freeze:
          return setIsFreezeConfirmModalOpen(true);

        case Action.Unfreeze:
          return updateMembership({
            endpoint: 'Unfreeze',
            data: {
              date: values.cancelDate
            },
            successMessage: 'Membership Unfrozen'
          });

        case Action.Cancel:
          return cancelMembership();

        case Action.Uncancel:
          return updateMembership({
            endpoint: 'Uncancel',
            data: {
              date: values.cancelDate
            },
            successMessage:
              status === Status.Canceled
                ? 'Removed Scheduled Cancellation'
                : 'Removed Cancellation'
          });

        case Action.Update:
          return updateMembership({
            endpoint: 'MembershipUpdate',
            data: {
              date: values.nextBillDate,
              price: Number(values.price)
            },
            successMessage: `Next Bill date set to ${
              values.nextBillDate
            }, Price set to ${Number(values.price).toFixed(2)}`
          });
      }
    }
  });

  const freezeConfirmationContinue = doProceed => {
    if (doProceed) {
      updateMembership({
        endpoint: 'Freeze',
        data: {
          date: formik.values.freezeDate,
          duration: formik.values.duration
        },
        successMessage: 'Membership Frozen'
      });
    }
    setIsFreezeConfirmModalOpen(false);
  };

  const submitButtonText = useMemo(() => {
    switch (formik.values.action) {
      case Action.Freeze:
        return 'Freeze Membership';
      case Action.Cancel:
        if (status === Status.Canceled) {
          return 'Submit';
        }
        return 'Cancel Membership';
      case Action.Uncancel:
        if (status === Status.Canceled) {
          return 'Remove Cancelation';
        } else {
          return 'Remove Scheduled Cancelation';
        }
      case Action.Unfreeze:
        return 'Unfreeze Membership';
    }
  }, [status, formik.values.action]);

  return (
    <Dialog open maxWidth="sm" fullWidth onClose={onClose}>
      <DialogTitle sx={{ paddingBottom: 0 }}>
        Edit Membership for {customerMembership.customerName}
      </DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent sx={{ paddingTop: 0 }}>
          {!isPaymentMethodOnFile && (
            <Typography variant="body4" sx={{ color: 'red' }}>
              To convert membership have a valid default payment method
            </Typography>
          )}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 2,
              marginBottom: 1
            }}
          >
            <Typography variant="body4" sx={{ color: 'text.secondary' }}>
              {customerMembership?.description} is {customerMembership?.status}
            </Typography>
            <Stack sx={{ textAlign: 'right' }}>
              <Typography
                variant="body4"
                sx={{ color: 'text.secondary' }}
              ></Typography>
              {customerMembership.nextBillDateReadable &&
                customerMembership.nextBillDateReadable !== '--' && (
                  <Typography variant="body4" sx={{ color: 'text.secondary' }}>
                    Next Bill Date is {customerMembership.nextBillDateReadable}
                  </Typography>
                )}
            </Stack>
          </Box>
          <Stack
            sx={{
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 1,
              backgroundColor: 'primary.25',
              marginInline: -2,
              padding: 2
            }}
          >
            <InputLabel sx={{ fontSize: '16px' }}>Edit Membership</InputLabel>
            <Select
              name="action"
              role="menu"
              value={formik.values.action}
              onChange={formik.handleChange}
              sx={{ minWidth: 200 }}
              autoWidth
            >
              {currentAction.value != Action.Update && (
                <MenuItem
                  value={currentAction.value}
                  sx={{ fontWeight: 400, fontSize: '14px' }}
                >
                  {currentAction.label}
                </MenuItem>
              )}

              {freezeAction && (
                <MenuItem
                  value={freezeAction.value}
                  sx={{ fontWeight: 400, fontSize: '14px' }}
                >
                  {freezeAction.label}
                </MenuItem>
              )}

              {cancelAction && (
                <MenuItem
                  value={cancelAction.value}
                  sx={{ fontWeight: 400, fontSize: '14px' }}
                >
                  {cancelAction.label}
                </MenuItem>
              )}
              {isPaymentMethodOnFile && customerMembership.isFrozen != 1 && (
                <MenuItem
                  value={convertAction.value}
                  sx={{ fontWeight: 400, fontSize: '14px' }}
                >
                  {convertAction.label}
                </MenuItem>
              )}

              {customerMembership.isFrozen != 1 && (
                <MenuItem
                  value={updateAction.value}
                  sx={{ fontWeight: 400, fontSize: '14px' }}
                >
                  {updateAction.label}
                </MenuItem>
              )}
            </Select>
          </Stack>
          <Stack
            sx={{
              paddingBlock: 2,
              gap: 2,
              alignItems: 'flex-start'
            }}
          >
            {formik.values.action === Action.Freeze && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <DateInput
                    name="freezeDate"
                    label="Freeze Start Date"
                    value={formik.values.freezeDate}
                    disabled={status === Status.Frozen}
                    onChange={formik.handleChange}
                    inputProps={{
                      min: new Date().toISOString().split('T')[0]
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl>
                    <InputLabel id="freeze-duration-select-label">
                      Freeze Duration
                    </InputLabel>
                    <Select
                      fullWidth
                      labelId="freeze-duration-select-label"
                      id="freeze-duration-select"
                      defaultValue={3}
                      name="duration"
                      style={{
                        justifyContent: 'flex-end'
                      }}
                      disabled={status === Status.Frozen}
                      onChange={formik.handleChange}
                      value={formik.values.duration}
                      label={'Freeze Duration'}
                    >
                      <MenuItem value={1}>1 Month</MenuItem>
                      <MenuItem value={2}>2 Months</MenuItem>
                      <MenuItem value={3}>3 Months</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body4">
                    Frozen Membership Restore Policy:
                    <BulletList>
                      <BulletListItem>
                        Written notice required to freeze your membership
                        account.
                      </BulletListItem>
                      <BulletListItem>
                        Membership can be frozen for 1, 2 and up to 3 months,
                        but only 1 time per year.
                      </BulletListItem>
                      <BulletListItem>
                        Accumulated membership credits are frozen and not
                        available to use while membership is frozen.
                      </BulletListItem>
                      <BulletListItem>
                        Accumulated membership credits will get a new expiration
                        date, based on remaining days when membership went on
                        freeze.
                      </BulletListItem>
                      <BulletListItem>
                        Membership pricing is honored while the account is
                        frozen.
                      </BulletListItem>
                    </BulletList>
                  </Typography>
                </Grid>
              </Grid>
            )}

            {formik.values.action === Action.Update &&
              customerMembership.isFrozen != 1 && (
                <>
                  <DateInput
                    name="nextBillDate"
                    label="Next Bill Date"
                    onChange={formik.handleChange}
                    value={formik.values.nextBillDate}
                    inputProps={{
                      min: new Date().toISOString().split('T')[0]
                    }}
                  />
                  <TextField
                    defaultValue={customerMembership?.billingAmount}
                    type="number"
                    name="price"
                    label="Billing Amount"
                    onChange={formik.handleChange}
                    value={formik.values.price}
                  />
                </>
              )}
            {formik.values.action === Action.Cancel && (
              <>
                <DateInput
                  name="cancelDate"
                  label="Cancel Start Date"
                  sx={{ minWidth: 200 }}
                  onChange={formik.handleChange}
                  value={formik.values.cancelDate}
                  inputProps={{
                    min: new Date().toISOString().split('T')[0]
                  }}
                />
                <MembershipCancelReasons
                  value={formik.values.cancelationReason}
                  onChange={formik.handleChange('cancelationReason')}
                  eventReasons={eventReasons['cancel']}
                />
                {formik.values.cancelDate !== customerMembership.cancelDate && (
                  <FormControlLabel
                    name="sendCancelationEmail"
                    onChange={formik.handleChange}
                    checked={formik.values.sendCancelationEmail}
                    control={<Checkbox color="primary" />}
                    labelPlacement="end"
                    sx={{ ml: -1, mt: -1 }}
                    label={
                      <Typography variant="body4">
                        Send Cancelation Email
                      </Typography>
                    }
                  />
                )}
                <Typography variant="body4">
                  Canceled Memberships:
                  <BulletList>
                    <BulletListItem>
                      The membership must have a three (3)-month minimum
                    </BulletListItem>
                    <BulletListItem>
                      Thirty (30)-day written notice to cancel must be made
                      in-person
                    </BulletListItem>
                    <BulletListItem>
                      Accumulated credits expire after thirty (30) days after
                      the final bank draft
                    </BulletListItem>
                  </BulletList>
                </Typography>
              </>
            )}
            {formik.values.action === Action.Uncancel && (
              <MembershipCancelReasons
                value={formik.values.cancelationReason}
                disabled={true}
                eventReasons={eventReasons['cancel']}
              />
            )}
            {formik.values.action === 'Convert' && (
              <ConvertMembershipModal
                oldMembership={customerMembership}
                setRefreshList={setRefreshList}
                setModalOpen={onClose}
                isPaymentMethodOnFile={isPaymentMethodOnFile}
                eventReasons={eventReasons['convert']}
              />
            )}
          </Stack>
        </DialogContent>

        {formik.values.action !== 'Convert' && (
          <DialogActions>
            <Button
              type="button"
              variant="outlined"
              onClick={onClose}
              disabled={formik.isSubmitting}
            >
              Keep Membership Unchanged
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={formik.isSubmitting}
              disabled={
                (!formik.dirty && formik.values.action !== Action.Freeze) ||
                (customerMembership.isFrozen &&
                  formik.values.action === Action.Freeze) ||
                (formik.values.action == Action.Cancel &&
                  !formik.values.cancelationReason)
              }
            >
              {submitButtonText || 'Submit'}
            </LoadingButton>
          </DialogActions>
        )}
      </form>
      {isFreezeConfirmModalOpen && (
        <FreezeMembershipConfirmation
          isOpen={isFreezeConfirmModalOpen}
          customerData={customerData}
          customerMembership={customerMembership}
          freezeStartDate={formik.values.freezeDate}
          freezeDuration={formik.values.duration}
          onClose={doProceed => freezeConfirmationContinue(doProceed)}
        />
      )}
    </Dialog>
  );
}

export default EditMembershipModal;

const MembershipCancelReasons: React.FC<any> = ({
  value,
  onChange,
  disabled,
  eventReasons
}) => {
  const menuItems = useMemo(() => {
    return eventReasons
      ? Object.entries(eventReasons).map(
          ([subCategory, subCategoryReasons]) => [
            <ListSubheader key={subCategory}>
              <Typography variant="body1" sx={{ mt: 1.5, mb: 0.25 }}>
                {reasonTypes[subCategory] || subCategory}
              </Typography>
            </ListSubheader>,
            ...(Array.isArray(subCategoryReasons)
              ? subCategoryReasons.map((reason, index) => (
                  <MenuItem
                    data-testid={`cancelation-reason-${index}`}
                    key={reason.id}
                    value={reason.id}
                  >
                    <Typography variant="body4">
                      -- {reason.description}
                    </Typography>
                  </MenuItem>
                ))
              : [])
          ]
        )
      : [];
  }, [eventReasons]);

  return (
    <FormControl error={!eventReasons} data-testid="cancel-reason-form">
      <InputLabel id="freeze-duration-select-label">
        Reason For Cancelation
      </InputLabel>

      <Select
        labelId="cancel-reason-code-label"
        id="cancel-reason-code"
        name="cancelationReason"
        label="Reason For Cancelation"
        onChange={e => onChange?.(e)}
        value={value}
        renderValue={selected => {
          const selectedReason = Object.values(eventReasons)
            .flat()
            .find(
              reason =>
                (reason as { id: string; description: string }).id === selected
            );
          return selectedReason
            ? (selectedReason as { id: string; description: string })
                .description
            : '';
        }}
        sx={{ minWidth: 200 }}
        disabled={disabled}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 400,
              overflowY: 'auto'
            }
          }
        }}
      >
        {menuItems}
      </Select>
    </FormControl>
  );
};
